/*
  ew-klaviyo-capture.js — single source of truth for newsletter capture.

  Both newsletter forms (footer ew-newsletter + tech-essay inline lead-cap)
  call window.EWKlaviyoCapture.subscribe(email, opts). Before this helper the
  identify + track logic was copy-pasted inline in each section; that
  duplication is now removed — this is the one implementation.

  Root cause it fixes (ESSAY-LEAK-1): the live "Email Welcome Series" flow
  triggers on "Added to List" for the Email List. The old inline forms only
  fired _learnq identify + track, which creates a profile and logs the event
  but NEVER adds the profile to a list — so no welcome email sends.
  subscribe() adds a real list subscription via Klaviyo's public Create Client
  Subscription endpoint. The list (TQkmSW) was switched to SINGLE opt-in on
  2026-07-27: the 202 add itself joins the profile to the list and triggers
  the flow — there is no confirmation step, so no caller may tell the
  subscriber to "check your inbox to confirm".

  Config is read from window.EW_KLAVIYO (set in theme.liquid):
    { companyId: <public key>, emailListId: <list id>, porscheListId: <list id> }
  Nothing is hardcoded here.

  A caller may target a DIFFERENT list than the sitewide one by passing
  opts.listId (WO-PORSCHE-PAGE-1 — the Porsche waitlist is its own list so the
  Welcome flow does not fire on it). It may also attach custom profile
  properties via opts.properties, and override the track event name
  (opts.event) and the funnel bucket (opts.funnel_source). All four are
  optional and every default reproduces the previous behavior exactly, so the
  two existing callers (ew-newsletter, tech-essays-article) are unaffected.

  Every sub-step (list subscription / _learnq identify+track / ewTrack funnel
  event) is independently guarded so one failing never blocks the others, and
  nothing ever throws. subscribe() resolves on a 2xx list-subscription response
  and rejects on a non-2xx / network / config error, so the form can show a
  real result instead of a fake timeout.
*/
(function () {
  'use strict';

  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions';
  var REVISION = '2026-04-15';

  /* Fire the legacy on-site identify + track. _learnq may not exist yet
     (klaviyo.js loads async, gated on consent) — guard and no-op if absent.
     Preserves today's segmentation payloads. */
  function pushLearnq(email, opts) {
    try {
      if (!window._learnq || typeof window._learnq.push !== 'function') return;
      var identify = { '$email': email, '$source': opts.source, '$consent': ['email'] };
      var track = { source: opts.source };
      if (opts.essay_handle) { identify.essay_handle = opts.essay_handle; track.essay_handle = opts.essay_handle; }
      if (opts.essay_title)  { identify.essay_title  = opts.essay_title;  track.essay_title  = opts.essay_title; }
      if (opts.livery)       { identify.livery       = opts.livery;       track.livery       = opts.livery; }
      if (opts.use_case)     { identify.use_case     = opts.use_case;     track.use_case     = opts.use_case; }
      if (opts.page_url)     { track.page_url        = opts.page_url; }
      /* Arbitrary custom profile properties (e.g. porsche_platform). Copied
         onto both identify and track so the value is segmentable either way.
         Absent for the two original callers → payloads stay byte-identical. */
      if (opts.properties) {
        for (var k in opts.properties) {
          if (!Object.prototype.hasOwnProperty.call(opts.properties, k)) continue;
          if (opts.properties[k] === null || opts.properties[k] === undefined || opts.properties[k] === '') continue;
          identify[k] = opts.properties[k];
          track[k] = opts.properties[k];
        }
      }
      window._learnq.push(['identify', identify]);
      window._learnq.push(['track', opts.event || 'Subscribed to Newsletter', track]);
    } catch (e) {}
  }

  /* G-1 funnel event. ewTrack is owned by ew-global.js and may not have
     loaded — guard and never throw. Maps the internal source string to the
     funnel's 'footer' | 'tech-essay' bucket. */
  function pushFunnel(opts) {
    try {
      if (!window.ewTrack) return;
      /* opts.funnel_source is an explicit override; without it the original
         two-bucket mapping is preserved exactly. A new surface MUST pass it,
         otherwise it would silently report itself as 'tech-essay'. */
      var bucket = opts.funnel_source || ((opts.source === 'storefront-footer') ? 'footer' : 'tech-essay');
      window.ewTrack('newsletter_signup', { source: bucket, method: 'klaviyo' });
    } catch (e) {}
  }

  /* POST the Create Client Subscription. Returns a Promise that resolves on
     a 2xx and rejects otherwise (non-2xx, network error, or missing config).
     Guarded so it never throws synchronously. */
  function postSubscription(email, opts) {
    try {
      var cfg = window.EW_KLAVIYO || {};
      /* Resolve the target list first: an explicit opts.listId wins, else the
         sitewide list. Guard on the RESOLVED id so a caller pointed at an
         unconfigured list fails loudly instead of silently subscribing the
         visitor to the sitewide Welcome flow. */
      var listId = opts.listId || cfg.emailListId;
      if (!cfg.companyId || !listId) {
        return Promise.reject(new Error('EW_KLAVIYO not configured'));
      }
      if (!window.fetch) {
        return Promise.reject(new Error('fetch unavailable'));
      }
      var profileAttrs = {
        email: email,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };
      /* Custom profile properties (e.g. { porsche_platform: '911 — 992' }).
         Omitted entirely when absent, so the original request body is
         unchanged for the two existing callers. */
      if (opts.properties) {
        var props = {};
        var any = false;
        for (var k in opts.properties) {
          if (!Object.prototype.hasOwnProperty.call(opts.properties, k)) continue;
          if (opts.properties[k] === null || opts.properties[k] === undefined || opts.properties[k] === '') continue;
          props[k] = opts.properties[k];
          any = true;
        }
        if (any) profileAttrs.properties = props;
      }
      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: opts.source,
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      };
      return window.fetch(ENDPOINT + '?company_id=' + encodeURIComponent(cfg.companyId), {
        method: 'POST',
        headers: {
          'revision': REVISION,
          'Content-Type': 'application/vnd.api+json'
        },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (res && res.ok) return res;
        throw new Error('Klaviyo subscription failed: ' + (res && res.status));
      });
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /*
    subscribe(email, opts) -> Promise
    opts = { source (required), essay_handle, essay_title, page_url, livery,
             use_case, listId, properties, event, funnel_source }

    The list subscription is the signal the caller waits on (its success/
    failure is what the form should reflect). identify+track and the funnel
    event are fired independently and never affect the returned promise.
  */
  function subscribe(email, opts) {
    opts = opts || {};
    /* Independently guarded side-effects — run regardless of the list
       subscription outcome so segmentation/analytics never depend on it. */
    pushLearnq(email, opts);
    pushFunnel(opts);
    /* The list subscription is the promise the form awaits. */
    return postSubscription(email, opts);
  }

  window.EWKlaviyoCapture = { subscribe: subscribe };
})();
